<template>
  <app-panel nested icon="euro" :title="panelTitle" flush>
    <template #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
      <app-icon-button icon="upload" label="Preise aus Excel importieren" @click="onImporting" />
      <app-icon-button icon="download" label="Excel-Export" @click="onExporting" />
      <app-icon-button
        v-if="permission == 'WRITE'"
        icon="plus"
        label="Tarif anlegen"
        @click="addRow"
      />
    </template>
    <!-- Spalten nicht umsortierbar: der Import schreibt über Spaltenindizes (cellValue) -->
    <dx-data-grid
      :id="gridRefName"
      :ref="gridRefName"
      :columns="columns"
      :data-source="dataSource"
      :show-borders="false"
      :show-column-lines="false"
      :show-row-lines="true"
      :hover-state-enabled="!gridTouch"
      :allow-column-resizing="!gridTouch"
      :allow-column-reordering="false"
      :scrolling="scrolling"
      :element-attr="gridAttr"
      :column-auto-width="true"
      :wordWrapEnabled="false"
      @toolbar-preparing="onToolbarPreparing"
      @editing-start="onEditingStart"
      @row-inserting="onDataChanged"
      @row-updating="onDataChanged"
      @row-removing="onDataChanged"
    >
      <DxSorting mode="none" />
      <dx-paging :page-size="100" />
      <dx-editing
        refresh-mode="full"
        :allow-adding="false"
        :allow-updating="permission == 'WRITE'"
        mode="batch"
      />
    </dx-data-grid>
    <app-save-bar
      v-if="hasChanges"
      class="panel-savebar"
      @save="saveChanges"
      @discard="revertChanges"
    />
    <input style="display: none" type="file" ref="fileInput" @change="importFile" />
  </app-panel>
</template>

<script>
import { DxDataGrid, DxEditing, DxPaging, DxSorting } from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import _ from "lodash";

export default {
  components: {
    DxDataGrid,
    DxEditing,
    DxPaging,
    DxSorting,
  },
  mixins: [DataGridMixin],
  props: ["tariffVersionId", "tariffZoneId", "tariffZoneName", "permission"],
  computed: {
    // Kartentitel „Preise · {Tarifzone}“
    panelTitle() {
      return this.tariffZoneName ? `Preise · ${this.tariffZoneName}` : "Preise";
    },
  },
  watch: {
    tariffVersionId() {
      this.refreshDataGrid();
    },
    tariffZoneId() {
      this.loadTicketTypes();
    },
  },
  data() {
    return {
      gridRefName: "priceDataGrid",
      hasChanges: false,
      gridAttr: {},
      tickettypes: [],
      columns: [],
      dataSource: new CustomStore({
        key: ["tariff_id", "tariff_version_id"],
        load: () =>
          axios.get(
            `${this.$store.state.baseUrl}price?tariff_version_id=${this.tariffVersionId}`
          ),
        update: (keys, updates) =>
          axios.post(`${this.$store.state.baseUrl}price`, {
            ...keys,
            ...updates,
            tariff_version_id: this.tariffVersionId,
          }),
        insert: (newPrice) =>
          axios.post(`${this.$store.state.baseUrl}price`, {
            ...newPrice,
            tariff_version_id: this.tariffVersionId,
          }),
      }),
    };
  },
  methods: {
    onToolbarPreparing(e) {
      // Remove all default toolbar items
      e.toolbarOptions.items = [];
    },
    onEditingStart(e) {
      if (e && e.data) {
        this.hasChanges = true;
      }
    },
    onDataChanged(e) {
      if (e && e.data) {
        this.hasChanges = true;
      }
    },
    addRow() {
      this.$refs[this.gridRefName].instance.addRow();
    },
    async saveChanges() {
      await this.$refs[this.gridRefName].instance.saveEditData();
      this.hasChanges = false;
    },
    revertChanges() {
      this.$refs[this.gridRefName].instance.cancelEditData();
      this.hasChanges = false;
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    getCaption(index) {
      try {
        return this.tickettypes.filter((ticket) => ticket.tariff_price_grade == index)[0]
          .name ;
      } catch (e) {
        return null;
      }
    },
    getOrder(index) {
      try {
        return this.tickettypes.filter((ticket) => ticket.tariff_price_grade == index)[0]
          .order_nr;
      } catch (e) {
        return 0;
      }
    },
    async generateColumns() {
      const columns = [
        {
          dataField: "tariff_id",
          caption: "ID",
          alignment: "left",
          allowEditing: true,
          order_nr: 0,
        },
        {
          dataField: "description",
          caption: "Tarif",
          alignment: "left",
          allowEditing: true,
          order_nr: 1,
        },
      ];

      for (let i = 0; i <= 30; i++) {
        columns.push({
          dataField: `${i}`,
          caption: this.getCaption(i),
          alignment: "right",
          dataType: "number",
          format: "######0.00 €",
          visible: !!this.getCaption(i),
          order_nr: this.getOrder(i) + 2,
        });
      }

      this.columns = columns.sort((a, b) => (a.tariff_price_grade > b.tariff_price_grade ? -1 : 1));
    },
    async loadTicketTypes() {
      this.tickettypes = (
        await axios.get(
          `${this.$store.state.baseUrl}tickettype?tariff_zone_id=${this.tariffZoneId}`
        )
      ).data;
      await this.generateColumns();
    },
    onExporting() {
      const tarifName = this.tariffZoneName;
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Preise");
      exportDataGrid({
        component: this.$refs[this.gridRefName].instance,
        worksheet: worksheet,
        customizeCell: function (options) {
          const excelCell = options;
          excelCell.font = { name: "Arial", size: 12 };
          excelCell.alignment = { horizontal: "left" };
        },
      }).then(function () {
        workbook.xlsx.writeBuffer().then(function (buffer) {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            `${tarifName}.xlsx`
          );
        });
      });
      // e.cancel = true;
    },
    onImporting() {
      this.$refs.fileInput.click();
    },
    async importFile(e) {
      console.log("importFile");
      const file = e.target.files[0];
      this.$refs.fileInput.value = "";
      const wb = new Workbook();
      const reader = new FileReader();
      const data = this.$refs[this.gridRefName].instance.getDataSource().items();
      console.log({data});

      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const buffer = reader.result;
        wb.xlsx.load(buffer).then((workbook) => {
          workbook.eachSheet((sheet) => {
            let reversedRows = [];
            sheet.eachRow((row, rowIndex) => {
              reversedRows.push({ row, rowIndex });
            });
            reversedRows = reversedRows.reverse();
            console.log({reversedRows});
            reversedRows.forEach((item) => {
              const row = item.row;
              const rowIndex = item.rowIndex;
              if (rowIndex == 1) {
                return;
              }
              const searchExp = { tariff_id: row.values[1] };
              const exists = _.some(data, searchExp);
              if (!exists) {
                // console.log("!exists");
                this.$refs[this.gridRefName].instance.addRow();
                this.$refs[this.gridRefName].instance.cellValue(
                  0,
                  "tariff_id",
                  row.values[1]
                );
                this.$refs[this.gridRefName].instance.cellValue(
                  0,
                  "description",
                  row.values[2]
                );
                for (let i = 1; i <= 32; i++) {
                  this.$refs[this.gridRefName].instance.cellValue(
                    0,
                    i+1,
                    row.values[i + 2]
                  );
                }
              } else {
                // console.log("exists");
                for (let i = 1; i <= 32; i++) {
                  const searchExpUpdate = { tariff_id: row.values[1] };
                  const float = parseFloat(row.values[i + 2]).toFixed(2);
                  if (isNaN(float)) {
                    continue;
                  }
                  searchExpUpdate[i] = `${float}`;
                  // console.log({ data, searchExpUpdate });
                  const existsUpdate = _.some(data, searchExpUpdate);
                  // console.log({ existsUpdate });
                  if (!existsUpdate) {
                    const index = _.findIndex(data, { tariff_id: row.values[1] });
                    // console.log({ index });
                    this.$refs[this.gridRefName].instance.cellValue(
                      index,
                      i+1,
                      row.values[i + 2]
                    );
                  }
                }
              }
            });
          });
        });
      };
    },
  },
};
</script>

<style scoped>
/* Speichern-Leiste als Fuß der Karte: .app-panel schneidet (overflow: hidden) und wird damit zum Bezugsrahmen von
   position: sticky; bottom: var(--sticky-unten) würde sie dort auf dem Handy um die untere Navigation anheben. */
.panel-savebar {
  position: static;
}
</style>
